import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import Loading from '../../Loading/loading';
import { AuthContext } from '../../Header/auth - context';
import useVisinaTabele from "../../custom/visinaTabele";
import './Kazne.css';

function formatDT(val) {
  if (!val) return '—';
  const s = String(val).replace('T', ' ').substring(0, 16);
  const [datePart, timePart] = s.split(' ');
  // vrednost koja nije datum (npr. već formatiran tekst ili '—') vraća se kakva jeste
  if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return String(val);
  const [y, mo, d] = datePart.split('-');
  return timePart ? `${d}.${mo}.${y} ${timePart}` : `${d}.${mo}.${y}`;
}

const ALL_COLUMNS = [
  { field: 'file',               header: 'Fajl' },
  { field: 'tip',                header: 'Tip' },
  { field: 'employee',           header: 'Vozač / Vozilo' },
  { field: 'cardNumber',         header: 'Kartica / Šasija' },
  { field: 'expiryDate',         header: 'Istek kartice' },
  { field: 'periodFrom',         header: 'Period od / Očitano' },
  { field: 'periodTo',           header: 'Period do' },
  { field: 'daysInserted',       header: 'Dana upisano' },
  { field: 'daysSkipped',        header: 'Dana preskočeno' },
  { field: 'placesInserted',     header: 'Lokacije' },
  { field: 'vehiclesInserted',   header: 'Vozila' },
  { field: 'eventsInserted',     header: 'Događaji' },
  { field: 'status',             header: 'Status' },
];

const DEFAULT_COLS = ALL_COLUMNS.map(c => c.field);

const KazneUcitavanje = () => {
  const { t } = useTranslation();
  const toast   = useRef(null);
  const fileRef = useRef(null);
  const auth    = useContext(AuthContext);

  const [load,    setLoad]    = useState(false);
  const [files,   setFiles]   = useState([]);
  const [results, setResults] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_COLS);
  /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
  const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([results, selectedColumns]);
  const [globalFilter,    setGlobalFilter]    = useState(null);
  const [first,           setFirst]           = useState(0);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.current.show({ severity: 'warn', summary: 'Upozorenje', detail: 'Izaberite DDD fajlove', life: 3000 });
      return;
    }

    setLoad(true);
    const newResults = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('ddd', file);

      try {
        const response = await fetch(
          process.env.REACT_APP_BACKEND_URL + '/tacho/upload',
          {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + auth.token },
            body: formData,
          }
        );

        const data = await response.json();

        if (response.ok) {
          /* Odgovor se razlikuje za karticu vozača i za jedinicu vozila (VU),
             pa se oba mapiraju na iste kolone tabele. */
          const jeVozilo = data.tip === 'VOZILO';
          newResults.push({
            file:               file.name,
            ok:                 true,
            tip:                jeVozilo ? 'Vozilo' : 'Vozač',
            employee:           jeVozilo ? (data.vozilo || '—') : data.employee,
            cardNumber:         jeVozilo ? (data.vin || '—')    : data.cardNumber,
            expiryDate:         jeVozilo ? '—' : (data.expiryDate || '—'),
            periodFrom:         data.periodFrom || (jeVozilo && data.lastDownload ? String(data.lastDownload).substring(0, 10) : '—'),
            periodTo:           data.periodTo || '—',
            daysInserted:       jeVozilo ? data.danaUpisano    : data.daysInserted,
            daysSkipped:        jeVozilo ? data.danaPreskoceno : data.daysSkipped,
            placesInserted:     data.placesInserted,
            vehiclesInserted:   data.vehiclesInserted,
            eventsInserted:     data.eventsInserted,
            status:             'OK',
            poruka:             data.message || null,
            svePostojalo:       !!data.svePostojalo,
            errorMsg:           null,
          });
        } else {
          /* I kad podaci nisu uneti (vozač/vozilo nije u sistemu), backend vraća
             ono što je pročitano iz fajla — popuni kolone da se vidi o kome je reč. */
          const jeVozilo = data.tip === 'VOZILO';
          newResults.push({
            file:       file.name,
            ok:         false,
            tip:        data.tip ? (jeVozilo ? 'Vozilo' : 'Vozač') : '—',
            employee:   jeVozilo ? (data.vozilo || '—') : (data.employee || '—'),
            cardNumber: jeVozilo ? (data.vin || '—')    : (data.cardNumber || '—'),
            expiryDate: jeVozilo ? '—' : (data.expiryDate || '—'),
            periodFrom: data.lastDownload ? String(data.lastDownload).substring(0, 10) : '—',
            periodTo:   '—',
            status:     data.message || 'Greška',
            errorMsg:   data.message,
          });
        }
      } catch {
        newResults.push({ file: file.name, ok: false, status: 'Greška pri slanju', errorMsg: 'Greška pri slanju fajla' });
      }
    }

    setResults(prev => [...prev, ...newResults]);
    setLoad(false);

    const failed = newResults.filter(r => !r.ok).length;
    const dup = newResults.filter(r => r.ok && r.svePostojalo).length;
    if (failed === 0 && dup === newResults.length) {
      toast.current.show({
        severity: 'info', summary: 'Već učitano',
        detail: `Svi podaci iz ${dup === 1 ? 'ovog fajla' : 'ovih fajlova'} su već ranije učitani — ništa nije duplirano.`,
        life: 5000,
      });
    } else if (failed === 0) {
      toast.current.show({
        severity: 'success', summary: 'Uspešno',
        detail: dup > 0
          ? `Obrađeno ${newResults.length}, od toga ${dup} već ranije učitano (bez dupliranja).`
          : `Svi fajlovi obrađeni (${newResults.length})`,
        life: 4000,
      });
    } else {
      toast.current.show({ severity: 'warn', summary: 'Delimično', detail: `${newResults.length - failed} uspešno, ${failed} neuspešno`, life: 4000 });
    }

    setFiles([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const statusBody = (row) => {
    if (row.ok) {
      if (row.svePostojalo) {
        return <span className="kazne-status-dup" title={row.poruka || ''}><i className="pi pi-info-circle" /> Već učitano</span>;
      }
      return <span className="kazne-status-ok" title={row.poruka || ''}><i className="pi pi-check" /> OK</span>;
    }
    return <span className="kazne-status-err" title={row.errorMsg}><i className="pi pi-times" /> {row.status}</span>;
  };

  const rowClass = (row) => ({ 'kazne-row-ok': row.ok, 'kazne-row-err': !row.ok });

  const colOptions = ALL_COLUMNS.map(c => ({ label: c.header, value: c.field }));
  const visibleCols = ALL_COLUMNS.filter(c => selectedColumns.includes(c.field));

  return (
    <div id="zaposleni_main">
      {load && <Loading />}
      <Toast ref={toast} />

      <div className="kazne-upload-section">
        <div className="kazne-upload-controls">
          <label className="kazne-file-label">
            <input
              ref={fileRef}
              type="file"
              accept=".ddd,.DDD"
              multiple
              onChange={handleFileChange}
              className="kazne-file-input"
            />
            <span className="kazne-file-btn">
              <i className="pi pi-folder-open" style={{ marginRight: 6 }} />
              Izaberi DDD fajlove
            </span>
          </label>

          {files.length > 0 && (
            <span className="kazne-file-count">
              {files.length} {files.length === 1 ? 'fajl izabran' : 'fajla izabrana'}
            </span>
          )}

          <Button
            label={t('mk.ucitajUBazu')}
            icon="pi pi-upload"
            className="p-button p-button-success"
            onClick={handleUpload}
            disabled={files.length === 0}
          />

          {results.length > 0 && (
            <Button
              label={t('mk.obrisiRezultate')}
              icon="pi pi-trash"
              className="p-button p-button-secondary"
              onClick={() => setResults([])}
            />
          )}
        </div>

        {files.length > 0 && (
          <ul className="kazne-file-list">
            {files.map((f, i) => (
              <li key={i} className="kazne-file-item">
                <i className="pi pi-file" style={{ marginRight: 6 }} />
                {f.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      {results.length > 0 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.8em', alignItems: 'center' }}>
            <div style={{ width: '35%' }}>
              <MultiSelect
                value={selectedColumns}
                options={colOptions}
                onChange={(e) => {
                  const sorted = ALL_COLUMNS.map(c => c.field).filter(f => e.value.includes(f));
                  setSelectedColumns(sorted);
                }}
                optionLabel="label"
                display="chip"
                placeholder={t('nova.odaberiteKolone')}
                style={{ width: '100%' }}
                showSelectAll
                filter
                filterPlaceholder="Pretraži kolone"
                maxSelectedLabels={3}
                selectedItemsLabel="{0} kolona odabrano"
              />
            </div>
          </div>

          <div ref={omotTabele}>
          <DataTable
            className="stavke-tabela"
            header={(
              <div className="export-buttons fakture-stavke-header">
                <span />
                <input type="search" className="p-inputtext p-component"
                       onInput={(e) => setGlobalFilter(e.target.value)}
                       placeholder={t('nova.phPretraga')} />
              </div>
            )}
            value={results}
            paginator
            rows={redovaTabele}
            rowsPerPageOptions={[redovaTabele, 25, 50]}
            first={first}
            onPage={(e) => setFirst(e.first)}
            globalFilter={globalFilter}
            scrollable
            scrollHeight={visinaTabele}
            emptyMessage={t('common.noDataTable')}
            resizableColumns
            columnResizeMode="expand"
            rowClassName={rowClass}
            className="turePregledTable stavke-tabela"
          >
            {visibleCols.map(col => {
              const dateFields = ['expiryDate', 'periodFrom', 'periodTo'];
              const body = col.field === 'status'
                ? statusBody
                : dateFields.includes(col.field)
                  ? (r) => formatDT(r[col.field])
                  : undefined;
              return (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable
                  style={{ minWidth: '120px' }}
                  body={body}
                />
              );
            })}
          </DataTable>
          </div>
        </div>
      )}
    </div>
  );
};

export default KazneUcitavanje;
